import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface MarketplaceProductCardProps {
  title: string;
  description?: string | null;
  price: string;
  actionLabel: string;
  onAction?: () => void;
  disabled?: boolean;
  loading?: boolean;
  imageUrl?: string | null;
  highlights?: string[];
  tags?: string[];
  footer?: ReactNode;
}

function getInitials(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return 'PT';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const first = parts[0][0] || '';
  const last = parts[parts.length - 1][0] || '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || 'PT';
}

export default function MarketplaceProductCard({
  title,
  description,
  price,
  actionLabel,
  onAction,
  disabled = false,
  loading = false,
  imageUrl,
  highlights,
  tags,
  footer,
}: MarketplaceProductCardProps) {
  const isDisabled = disabled || loading;
  const [imageFailed, setImageFailed] = useState(false);
  const initials = useMemo(() => getInitials(title), [title]);
  const showImage = Boolean(imageUrl && !imageFailed);

  return (
    <div className="flex h-full flex-col justify-between rounded-3xl bg-white/90 p-5 shadow-soft ring-1 ring-sand/40">
      <div className="space-y-4">
        {showImage ? (
          <div className="overflow-hidden rounded-2xl border border-shell/70 bg-shell/20">
            <img
              src={imageUrl!}
              alt=""
              className="h-32 w-full object-cover"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          </div>
        ) : (
          <div className="flex h-32 w-full items-center justify-center rounded-2xl border border-shell/40 bg-gradient-to-br from-sand/40 via-shell/30 to-white text-2xl font-semibold uppercase tracking-wide text-cocoa/50">
            {initials}
          </div>
        )}

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-cocoa">{title}</h3>
          {description ? <p className="text-sm text-cocoa/70">{description}</p> : null}
        </div>

        {Array.isArray(highlights) && highlights.length ? (
          <ul className="list-disc space-y-1 pl-5 text-sm text-cocoa/70">
            {highlights.slice(0, 6).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="mt-6 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-cocoa">{price}</div>
          <button
            type="button"
            onClick={onAction}
            disabled={isDisabled || !onAction}
            className="inline-flex w-full justify-center rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-terracotta/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {loading ? 'Processing…' : actionLabel}
          </button>
        </div>
        {Array.isArray(tags) && tags.length ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 6).map((tag) => (
              <span key={tag} className="rounded-full bg-sand px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-cocoa/70">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        {footer ? <div className="text-xs text-cocoa/60">{footer}</div> : null}
      </div>
    </div>
  );
}
